/* =========================================================================
 * WHICH GOVERNING BODY AM I ACTUALLY ASSIGNING? — the qualified keys on a card.
 *
 * Brian: "in governing body - i need to be able to add a rep to a governing
 * body." The hard part is not the control. It is that a CARD IS NOT A BODY.
 *
 * A card is a registry entity, keyed `TX-UIL`, `CA-CIF`. The ASSIGNMENT is
 * keyed on the string the records carry in `governing_body`, and the two are
 * not one-to-one in either direction. Measured on production
 * 2026-10-14T16:46:42Z by a full page-through of all 15,854 records:
 *
 *   · The Texas card's records carry SIXTEEN different UIL body strings:
 *     `UIL (public)` 168, `University Interscholastic League` 14, eight
 *     `UIL (1A..6A)` / `(unclassified)` / `(cross-classification)` keys
 *     totalling 22, and six `University Interscholastic League (nA)` keys
 *     totalling 6. 213 UIL records in all.
 *   · The California card's records carry eleven: ten `CIF ... Section` keys
 *     holding 882 records between them, plus `CIF (public)` 159.
 *
 * So "assign this card" is not a well-formed instruction, and a control that
 * offered it would quietly assign one of the sixteen and miss the rest — or,
 * worse, write a key nothing resolves to and report success. This module
 * derives the REAL keys, with their counts and their current owners, so the
 * person choosing can see what they are choosing.
 *
 * ⚠️ THE QUALIFIER IS NEVER STRIPPED AND THE BARE ACRONYM IS NEVER OFFERED.
 * 7,197 records (45.4%) carry a `(public)` / `(private/independent)` /
 * `Section` qualifier, and collapsing it fuses 39 groups of keys — including
 * `ACAA (Christian)` (22 records, Alabama, Chris Powell) with `ACAA` (11
 * records, Canada, Zach Muller), which are the Alabama Christian and the
 * ATLANTIC Collegiate athletic associations. The server refuses a bare
 * acronym; this module makes sure one is never put in front of a person to
 * click in the first place.
 *
 * ⚠️ IT READS THE CARD'S OWN ROWS, not the universe. Same rule the assignment
 * stamp already follows: a second join would be a second answer to one
 * question, and a breakdown that counts a different set from the number beside
 * it is the reconciliation failure this page exists to remove.
 *
 * NO MONEY. Every figure here is a record count.
 * ========================================================================= */
import { repOn } from './bodyAssignment.js'
import { isDefectiveName } from '../../lib/bodyChampionships.js'

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/* The two fields, and they are NOT interchangeable. Measured 2026-10-14 by a
 * full page-through of all 15,854 records against the 221 keys in the
 * backend's `assignment/sheet.json`:
 *
 *     governing_body IS a sheet key : 2,583 records
 *     sr_conference  IS a sheet key : 6,202 records
 *     NEITHER matches               : 7,069 records  (44.6%)
 *
 * because they hold different KINDS of name, consistently:
 *
 *     governing_body (QUALIFIED)      sr_conference (FULL NAME)
 *     TAPPS (private/independent)     Texas Association of Private and Parochial Schools
 *     CIF Southern Section            California Interscholastic Federation
 *     ACAA (Christian)                Alabama Christian Athletic Association
 *
 * `governing_body` is the one that disambiguates, so it is the one the roster
 * is keyed on and the one this offers FIRST. `sr_conference` is offered as a
 * SEPARATE, clearly-labelled key rather than as a fallback value of the same
 * key — assigning "California Interscholastic Federation" and assigning "CIF
 * Southern Section" are different decisions and must not look like one. */
export const QUALIFIED_FIELD = 'governing_body'
export const FULLNAME_FIELD = 'sr_conference'

/* A bare token that means more than one real body. Mirrors JURISDICTION in the
 * backend's assignment/body-key.js — kept in sync by `verify-body-roster.mjs`,
 * which fails if this list and the server's disagree, because a client that
 * offers a key the server refuses is a button that cannot work. */
export const AMBIGUOUS = new Set(['ACAA', 'MHSAA', 'MIAA', 'CHSAA', 'CCAA', 'AAA', 'WIAA', 'IHSAA'])

/** Is this string safe to offer as an assignment key? */
export function offerable(body) {
  const b = S(body)
  if (!b) return { ok: false, why: 'the record names no governing body' }
  if (b === 'Unspecified') return { ok: false, why: '"Unspecified" is not an organisation — this is a data repair, not an assignment' }
  if (AMBIGUOUS.has(b.toUpperCase())) {
    return { ok: false, why: `"${b}" is a bare acronym that means more than one governing body, held by different reps. The qualified string is the assignable key.` }
  }
  return { ok: true }
}

/**
 * Every assignable body key on this card, from its own rows.
 *
 * Returns `{ keys, unofferable, rows }` where each key is:
 *   body        the exact string to send
 *   field       'governing_body' (qualified) or 'sr_conference' (full name)
 *   records     how many of this card's rows carry it
 *   reps        [{ rep, count }] who holds them TODAY, count desc
 *   unassigned  rows with no rep
 *
 * ⚠️ `unofferable` IS RETURNED RATHER THAN FILTERED AWAY. A card whose records
 * carry only "Unspecified" would otherwise render an empty control that looks
 * broken. Saying which strings were excluded, and why, is the difference
 * between a control with nothing to offer and a control that is not working —
 * and on a screen those are the same thing unless one of them says so.
 */
export function bodyKeysFor(rows = []) {
  const byKey = new Map()
  const unofferable = new Map()

  const add = (body, field, row) => {
    const k = `${field}::${body}`
    if (!byKey.has(k)) byKey.set(k, { body, field, records: 0, counts: new Map(), unassigned: 0 })
    const e = byKey.get(k)
    e.records += 1
    const rep = repOn(row)
    if (rep) e.counts.set(rep, (e.counts.get(rep) || 0) + 1)
    else e.unassigned += 1
  }

  for (const row of rows) {
    /* The truncated records are not titles and are not counted as held by the
     * stamp, so they are not counted here either — otherwise this control's
     * numbers and the card's numbers disagree. */
    if (isDefectiveName(row)) continue
    const raw = row?.raw || row || {}
    const gb = S(raw[QUALIFIED_FIELD])
    const cf = S(raw[FULLNAME_FIELD])

    const g = offerable(gb)
    if (gb && g.ok) add(gb, QUALIFIED_FIELD, row)
    else if (gb) unofferable.set(`${gb} — ${g.why}`, (unofferable.get(`${gb} — ${g.why}`) || 0) + 1)

    /* The full name is offered only when it is NOT the same decision as the
     * qualified key already offered. Where both exist they are two different
     * keys on purpose: `CIF Southern Section` is one section, `California
     * Interscholastic Federation` is all ten plus the state office. */
    const c = offerable(cf)
    if (cf && c.ok && cf !== gb) add(cf, FULLNAME_FIELD, row)
    else if (cf && !c.ok) unofferable.set(`${cf} — ${c.why}`, (unofferable.get(`${cf} — ${c.why}`) || 0) + 1)
  }

  const keys = [...byKey.values()].map((e) => ({
    body: e.body,
    field: e.field,
    records: e.records,
    unassigned: e.unassigned,
    reps: [...e.counts.entries()]
      .map(([rep, count]) => ({ rep, count }))
      /* Count desc then name asc — STABLE, so two renders cannot disagree
       * about who the dominant holder is. */
      .sort((a, b) => b.count - a.count || a.rep.localeCompare(b.rep)),
  })).sort((a, b) => (
    /* The qualified field first, because it is the assignable one and the one
     * that cannot be ambiguous. Then by size. */
    (a.field === QUALIFIED_FIELD ? 0 : 1) - (b.field === QUALIFIED_FIELD ? 0 : 1)
    || b.records - a.records
    || a.body.localeCompare(b.body)
  ))

  return {
    keys,
    unofferable: [...unofferable.entries()].map(([why, count]) => ({ why, count })).sort((a, b) => b.count - a.count),
    /* ⚠️ THE RECONCILIATION. Rows counted under the QUALIFIED field must add up
     * to the rows the card holds, or a key is missing from this list and the
     * control would silently not cover some of the card's records. Reported,
     * never assumed — the same posture as the assignment stamp's own check. */
    qualifiedRecords: keys.filter((k) => k.field === QUALIFIED_FIELD).reduce((n, k) => n + k.records, 0),
  }
}

/**
 * How many of this card's records would a given key actually decide?
 *
 * ⚠️ IT IS NOT THE SAME AS `records` ON THE KEY, AND THE DIFFERENCE IS WHAT
 * STOPS THIS CONTROL LYING. A sport lane outranks the body rule server-side:
 * cheer and dance go to the cheer lead whatever the body says, and so do
 * motorsports, rodeo, marching arts, equestrian and outdoor. So a key holding
 * 40 records of which 12 are cheer will only ever decide 28 of them, and a
 * control promising 40 would be wrong by twelve every time.
 *
 * This does not reimplement the lane test — it reads the OWNERS the lane has
 * already produced, which is the honest proxy available on the client, and it
 * labels the figure as an estimate rather than a promise.
 */
export function wouldDecide(key, laneLeads = []) {
  const leads = new Set(laneLeads)
  const inLane = key.reps.filter((r) => leads.has(r.rep)).reduce((n, r) => n + r.count, 0)
  return {
    records: key.records,
    likelyLaneHeld: inLane,
    estimate: Math.max(0, key.records - inLane),
    note: inLane
      ? `${inLane} of these are held by a sport-lane lead (cheer, motorsports, rodeo, marching arts, `
        + 'equestrian, outdoor). A sport lane outranks the body rule on the server, so those stay where '
        + 'they are whatever this body is set to.'
      : '',
  }
}

/** The lane leads, as display names. Mirrors SPORT_LANES in the backend. */
export const LANE_LEADS = ['Jennifer Loveday-Donovan', 'Jake Highter', 'Abigail Mancuso']
